import { TwoUIBiCustomClick, useTwoUITelemetry } from "@amx/ux-twoui-telemetry";
import { useTheme } from "@amx/ux-twoui-theming";
import { Dropdown as FluentDropdown, DropdownMenuItemType, ICalloutProps, IDropdown, IDropdownOption, IDropdownProps, IDropdownStyles, ResponsiveMode, Spinner, SpinnerSize, Stack } from "@fluentui/react";
import React, { useRef } from "react";
import { anchorPositionToFluentMapping } from "../../shared/constants";
import { useComponentLogger } from "../../shared/logging";
import { validateProps } from "../../shared/props";
import { highContrastMedia } from "../../styles";
import { ErrorCallout } from "../ErrorCallout/ErrorCallout";
import { ShimmerProgressView } from "../Shimmer/ShimmerProgressView/ShimmerProgressView";
import { DropdownOption, DropdownProps } from "./Dropdown.types";
import { DropdownShimmer } from "./DropdownShimmer/DropdownShimmer";

const onRenderCaretDownRenderSpinner = (): JSX.Element => {
    return (
        <Stack verticalFill={true} verticalAlign="center">
            <Spinner size={SpinnerSize.medium} />
        </Stack>
    );
};

export const Dropdown: React.FC<DropdownProps> = ({
    "aria-label": ariaLabel,
    "data-bi-prefix": biPrefix,
    value,
    pageArea,
    placeholder,
    options,
    onChange,
    label,
    width,
    calloutWidth,
    calloutPosition = "bottom-center",
    alignTitle,
    maxHeight,
    descriptions = false,
    disabled = false,
    multiSelect = false,
    dropdownStyle = "default",
    title,
    componentRef,
    calloutPreventDismissOnScroll = false,
    required,
    disableResponsiveMode = false,
    ...otherProps
}) => {
    const twoUITelemetry = useTwoUITelemetry();
    useComponentLogger("Dropdown", otherProps.id);
    validateProps("Dropdown", otherProps);

    const theme = useTheme();
    const dropdownRef = useRef<HTMLDivElement>(null);
    const enabledBackground = dropdownStyle === "borderless" ? theme.semanticColors.bodyBackground : theme.palette.neutralHigh;

    const dropdownStyles: Partial<IDropdownStyles> = {
        title: {
            backgroundColor: disabled ? theme.palette.neutralLighterAlt : enabledBackground,
            borderColor: dropdownStyle === "borderless" ? "transparent" : undefined,
            textAlign: alignTitle
        },
        dropdownItem: {
            backgroundColor: theme.palette.neutralHigh,
            height: "auto"
        },
        dropdownItemSelected: {
            height: "auto"
        },
        dropdownItemDisabled: {
            backgroundColor: theme.palette.neutralHigh,
            height: "auto"
        },
        dropdownOptionText: { overflow: "visible", whiteSpace: "normal" },
        dropdownItemSelectedAndDisabled: {
            [highContrastMedia.active]: {
                backgroundColor: "transparent",
                border: "1px solid highlight"
            },
            height: "auto"
        }
    };

    if (width) {
        dropdownStyles.dropdown = { width: width };
    }

    // if descriptions are enabled, pass custom onRenderOptions with styling
    let onRenderOptions: any;
    if (descriptions) {
        // set items to fit container to enable justifyContent
        dropdownStyles.dropdownItem = {
            backgroundColor: theme.palette.neutralHigh,
            selectors: {
                ".ms-Button-flexContainer": {
                    width: "100%"
                }
            },
            height: "auto"
        };
        dropdownStyles.dropdownItemSelected = {
            selectors: {
                ".ms-Button-flexContainer": {
                    width: "100%"
                },
                height: "auto"
            }
        };
        onRenderOptions = (option: DropdownOption) => {
            const divStyle: React.CSSProperties = { "width": "100%", "display": "flex", "justifyContent": "space-between" };
            return (
                <div style={divStyle}>
                    <span>{option.text}</span>
                    {option.description ? <span>{option.description}</span> : <></>}
                </div>
            );
        };
    }

    // casting onChange to expected type
    const localOnChange = (event: React.FormEvent<HTMLDivElement>, option?: IDropdownOption, index?: number) => {
        if (option) {

            // Explicitly send a BI event, since automatic click tracking can't fire for custom elements.
            // create the custom click event
            const clickEvent: TwoUIBiCustomClick = {
                clickId: `${biPrefix}-${option["id"]}`,
                pageArea: pageArea
            };
            // send the event
            twoUITelemetry.reportClick(clickEvent);

            onChange(event, { key: option.key.toString(), text: option.text, ariaLabel: option.ariaLabel!, id: option["id"]! }, option.selected, index);
        } else {
            onChange(event, undefined, undefined, index);
        }
    };

    const keyProps: Partial<IDropdownProps> = {};
    if (multiSelect) {
        keyProps.selectedKeys = Array.isArray(value) ? value.slice() : value === null ? value : [value];
    } else {
        keyProps.selectedKey = Array.isArray(value) ? value.slice() : value;
    }

    const updatedOptions = options.map((option, index) => {
        if (option.itemType === "divider") {
            return { key: `divider_${index}`, text: "", itemType: DropdownMenuItemType.Divider };
        } else {
            return { ...option, itemType: DropdownMenuItemType.Normal };
        }
    });

    const scrollEvent = (ev: Event | React.FocusEvent<Element> | React.KeyboardEvent<Element> | React.MouseEvent<Element, MouseEvent>) => {
        // This returns true when scrolling, preventing an accidental close of the Callout.
        return ev.type === "scroll";
    };

    //Reference: https://github.com/microsoft/fluentui/issues/8748
    const calloutProps: ICalloutProps = {
        calloutMaxHeight: maxHeight,
        directionalHint: anchorPositionToFluentMapping[calloutPosition!],
        preventDismissOnEvent: calloutPreventDismissOnScroll ? scrollEvent : undefined
    };

    let baseProps: IDropdownProps = {
        placeholder,
        options: updatedOptions,
        onChange: localOnChange,
        disabled,
        required,
        label,
        dropdownWidth: calloutWidth,
        ariaLabel,
        calloutProps,
        styles: dropdownStyles,
        onRenderOption: onRenderOptions,
        multiSelect,
        componentRef,
        ...keyProps
    };

    if (title !== undefined) {
        baseProps.onRenderTitle = () => <>{title}</>;
    }

    if (disableResponsiveMode) {
        baseProps.responsiveMode = ResponsiveMode.unknown;
    }

    if (otherProps.operationData) {

        const {
            operationData,
            ...otherPropsWithoutOperationData
        } = otherProps;

        baseProps = {
            ...baseProps,
            // Merge all other props without onRenderCaretDown, errorMessage,
            // operationName, and componentName
            ...otherPropsWithoutOperationData
        };

        let errorView: JSX.Element;

        if (operationData.errorProps.errorType === "callout") {
            errorView = (
                <>
                    <div ref={dropdownRef} style={{ width: width }}>
                        <FluentDropdown {...baseProps} />
                    </div>
                    <ErrorCallout
                        calloutPosition="bottom-center"
                        targetOrTriggerButton={dropdownRef}
                        errorMessageAriaLabel={operationData.errorProps.errorCallout.errorMessageAriaLabel}
                        errorMessage={operationData.errorProps.errorCallout.errorMessage}
                    />
                </>
            );
        } else {
            errorView = <FluentDropdown {...baseProps} errorMessage={operationData.errorProps.errorMessage} />;
        }

        const updatingStyles = {
            ...dropdownStyles,
            title: {
                backgroundColor: theme.palette.neutralHigh,
                border: `1px solid ${theme.palette.neutralQuaternary}`,
                textAlign: alignTitle
            }
        };

        return (
            <ShimmerProgressView
                operationData={operationData.progressViewOperationData}
                firstLoadView={(
                    <DropdownShimmer />
                )}
                updatingView={(
                    <FluentDropdown
                        {...baseProps}
                        disabled={true}
                        onRenderCaretDown={onRenderCaretDownRenderSpinner}
                        styles={updatingStyles}
                    />
                )}
                finishedView={(
                    <FluentDropdown {...baseProps} />
                )}
                updateFailedView={errorView}
                failedView={undefined}
            />
        );
    } else {
        baseProps = {
            ...baseProps,
            // Merge all other props
            ...otherProps
        };
        return (
            <FluentDropdown {...baseProps} />
        );
    }
};

export const getDropdownRef = (): React.RefObject<IDropdown> => {
    return React.createRef<IDropdown>();
};
